"use client";

import type { FavoriteVehicleTypeDTO, FavoritesPageDTO, User } from "@packages/domain";
import { useInfiniteQuery } from "@tanstack/react-query";
import { favoritesService } from "@/app";

export const FAVORITES_QUERY_KEY = "FAVORITES_QUERY";

export type UseFavoritesResourceParams = {
  user: User | null;
  vehicleType: FavoriteVehicleTypeDTO;
  enabled: boolean;
};

/**
 * Paginated favorites for a single vehicle type, backed by an infinite query.
 *
 * Each page is fetched on demand (as the user scrolls), so a user with many
 * favorites does not pay the cost of loading everything up front (ENO-10086).
 */
export function useFavoritesResource({
  user,
  vehicleType,
  enabled,
}: UseFavoritesResourceParams) {
  return useInfiniteQuery<FavoritesPageDTO>({
    queryKey: [FAVORITES_QUERY_KEY, vehicleType, user?.id ?? null],
    queryFn: ({ pageParam }) =>
      favoritesService.getByType(user, vehicleType, pageParam as number),
    initialPageParam: 1,
    // Stop based on how many items have actually been loaded vs the total
    // `count`, rather than a page-size guess. The API caps how many items it
    // returns per request, so this is robust whether or not it honors the
    // requested page size or reports `pages` (ENO-10086). The empty-page guard
    // prevents an unbounded loop if `count` is ever stale/inconsistent.
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.favorites.length === 0) return undefined;
      const loaded = allPages.reduce(
        (sum, page) => sum + page.favorites.length,
        0
      );
      return loaded < lastPage.count ? lastPage.page + 1 : undefined;
    },
    enabled: enabled && !!user,
  });
}
